/* NOTICE — add-ons strip (snippets/notice-addons.liquid).

   1. Variant choice on an add-on swaps its price for the markup Liquid rendered for that
      variant (a <span class=money>, so BUCKS converts it like every other price on the
      page) and its image when that variant has one of its own.
   2. QUICK ADD posts that one line to the Ajax cart. The bag updates in place: the cart
      drawer and the header count are re-rendered from the sections the add response
      carries (the same replacement cart-drawer.js renderContents does), and the drawer
      stays shut, or opens when the block is set to "Open the cart drawer".
   3. ADD BOTH TO BAG posts the main product (the variant, quantity and any properties in
      the product form of the same column) and the add-on in one request, then opens the
      drawer the way ADD TO CART does.
   4. Every successful add publishes PUB_SUB_EVENTS.cartUpdate with source 'product-form',
      once per line added, the way the buy buttons do, so the same listeners run: Pinterest
      AddToCart (snippets/analytics-scripts.liquid), GoKwik's India / international checkout
      switch (snippets/gokwik.liquid), cart.js (which skips its own drawer refetch for this
      source). `origin: 'notice-addons'` tells these adds apart. Meta and GA4 AddToCart come
      from Shopify's own cart events, which fire on any /cart/add request.

   Nothing is added without a click and nothing is pre-ticked. */
(function () {
  if (customElements.get('notice-addons')) return;

  const MIN_BUSY_MS = 420; // product-form.js uses the same floor so the spinner always paints
  const ADDED_MS = 2600;
  const ERROR_MS = 6000;

  // constants.js declares PUB_SUB_EVENTS with `const`: it lives in the shared global
  // lexical scope, not on window (see the note in assets/shoppable-video.js).
  const pubSubEvents = () =>
    (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS) || null;

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  class NoticeAddons extends HTMLElement {
    connectedCallback() {
      this.statusEl = this.querySelector('[data-addons-status]');
      this.bothButton = this.querySelector('[data-addons-both]');
      if (this.bothButton) {
        const label = this.bothButton.querySelector('[data-addons-both-label]');
        this.bothButton.dataset.label = label ? label.textContent.trim() : '';
      }
      this.timers = new Map();
      this.busy = false;

      this.handleChange = this.onChange.bind(this);
      this.handleClick = this.onClick.bind(this);
      this.addEventListener('change', this.handleChange);
      this.addEventListener('click', this.handleClick);

      const events = pubSubEvents();
      if (events && typeof subscribe === 'function') {
        this.unsubscribe = subscribe(events.variantChange, this.onMainVariantChange.bind(this));
      }

      // A reload, or Back without the page cache, can bring back a choice the server did not
      // render: Chrome and Firefox restore form state while parsing, with no change event, so a
      // card would show one variant's price and add another. autocomplete="off" on the controls
      // stops most of it; this catches the rest.
      this.querySelectorAll('[data-addon]').forEach((item) => {
        if (this.wasRestored(item)) this.syncVariant(item);
      });
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('click', this.handleClick);
      if (this.unsubscribe) this.unsubscribe();
      this.timers.forEach((timer) => clearTimeout(timer));
      this.timers.clear();
    }

    /* ---------- events ---------- */

    onChange(event) {
      const item = event.target.closest('[data-addon]');
      if (!item || !this.contains(item)) return;
      if (event.target.matches('[data-addon-variant], [data-addon-select]')) {
        this.syncVariant(item);
      } else if (event.target.matches('[data-addon-qty]')) {
        this.clampQuantity(item);
      }
    }

    onClick(event) {
      const add = event.target.closest('[data-addon-add]');
      if (add && this.contains(add)) {
        this.quickAdd(add.closest('[data-addon]'), add);
        return;
      }
      const both = event.target.closest('[data-addons-both]');
      if (both && this.contains(both)) {
        this.addBoth(both);
        return;
      }
      const step = event.target.closest('[data-qty-step]');
      if (step && this.contains(step)) this.stepQuantity(step);
    }

    // product-info publishes variantChange after it has swapped the buy buttons for the new
    // variant (ADD TO CART or REGISTER INTEREST, enabled or not), so the buttons on the page are
    // the truth: they also count quantity rules, which variant.available does not.
    // "Add both" only makes sense while the main product can be bought.
    onMainVariantChange({ data } = {}) {
      if (!this.bothButton || !data || String(data.sectionId) !== this.dataset.sectionId) return;
      this.bothButton.hidden = !(data.variant && data.variant.available && this.mainBuyable());
      this.reset(this.bothButton);
      this.hideError(this.querySelector('[data-addons-both-error]'));
    }

    /* ---------- reading an add-on ---------- */

    selected(item) {
      const select = item.querySelector('[data-addon-select]');
      if (select) return select.selectedOptions[0] || null;
      return (
        item.querySelector('input[data-addon-variant]:checked') ||
        item.querySelector('input[type="hidden"][data-addon-variant]')
      );
    }

    quantity(item, variant) {
      const min = Math.max(1, parseInt(variant.dataset.min, 10) || 1);
      const step = Math.max(1, parseInt(variant.dataset.step, 10) || 1);
      const max = parseInt(variant.dataset.max, 10) || Infinity;
      const input = item.querySelector('[data-addon-qty]');
      let quantity = input ? parseInt(input.value, 10) : min;
      if (!Number.isFinite(quantity) || quantity < min) quantity = min;
      if (quantity > max) quantity = max;
      quantity -= (quantity - min) % step;
      return quantity;
    }

    wasRestored(item) {
      const select = item.querySelector('[data-addon-select]');
      const choices = select
        ? [...select.options]
        : [...item.querySelectorAll('input[type="radio"][data-addon-variant]')];
      const moved = (control) =>
        control.matches('option') ? control.selected !== control.defaultSelected : control.checked !== control.defaultChecked;
      if (choices.some(moved)) return true;
      const qty = item.querySelector('[data-addon-qty]');
      return !!qty && qty.value !== qty.defaultValue;
    }

    describe(item, variant) {
      const title = item.dataset.title || '';
      const variantTitle = variant && variant.dataset.variantTitle;
      return variantTitle ? `${title}, ${variantTitle}` : title;
    }

    syncVariant(item) {
      const variant = this.selected(item);
      if (!variant) return;

      const price = item.querySelector('[data-addon-price]');
      if (price && variant.dataset.price) price.innerHTML = variant.dataset.price;

      const image = item.querySelector('.nt-addon__img');
      if (image) {
        if (image.dataset.baseSrc === undefined) {
          image.dataset.baseSrc = image.getAttribute('src') || '';
          image.dataset.baseSrcset = image.getAttribute('srcset') || '';
        }
        const src = variant.dataset.image || image.dataset.baseSrc;
        const srcset = variant.dataset.image ? variant.dataset.imageSrcset || '' : image.dataset.baseSrcset;
        if (src && image.getAttribute('src') !== src) {
          if (srcset) image.setAttribute('srcset', srcset);
          else image.removeAttribute('srcset');
          image.setAttribute('src', src);
        }
      }

      const input = item.querySelector('[data-addon-qty]');
      if (input) {
        input.min = variant.dataset.min || 1;
        input.step = variant.dataset.step || 1;
        if (variant.dataset.max) input.max = variant.dataset.max;
        else input.removeAttribute('max');
        this.clampQuantity(item);
      }

      const button = item.querySelector('[data-addon-add]');
      if (button) {
        this.reset(button);
        const available = variant.dataset.available === 'true';
        button.disabled = !available;
        this.setLabel(button, available ? this.dataset.addLabel : this.dataset.soldOutLabel);
      }
      this.hideError(item.querySelector('[data-addon-error]'));
    }

    stepQuantity(stepButton) {
      const item = stepButton.closest('[data-addon]');
      const input = item && item.querySelector('[data-addon-qty]');
      const variant = item && this.selected(item);
      if (!input || !variant) return;
      const step = Math.max(1, parseInt(variant.dataset.step, 10) || 1);
      const direction = parseInt(stepButton.dataset.qtyStep, 10) || 0;
      input.value = (parseInt(input.value, 10) || 0) + direction * step;
      this.clampQuantity(item);
    }

    clampQuantity(item) {
      const input = item.querySelector('[data-addon-qty]');
      const variant = this.selected(item);
      if (input && variant) input.value = this.quantity(item, variant);
    }

    /* ---------- the main product (for "Add both") ---------- */

    mainForm() {
      const column = this.closest('.product__details--desk, .product__details--mobile');
      return (
        (column && column.querySelector('form[data-type="add-to-cart-form"]')) ||
        document.getElementById(this.dataset.productFormId)
      );
    }

    mainSubmit() {
      const form = this.mainForm();
      // sold out = the buy buttons render REGISTER INTEREST, which is not a submit button
      return form ? form.querySelector('[type="submit"][name="add"]') : null;
    }

    mainBuyable() {
      const submit = this.mainSubmit();
      return !!submit && !submit.disabled;
    }

    // product-form.js and the sticky bars mark an add in flight with aria-disabled / aria-busy,
    // not the disabled attribute (iOS paints a disabled button grey).
    mainBusy() {
      const submit = this.mainSubmit();
      return !!submit && (submit.getAttribute('aria-disabled') === 'true' || submit.getAttribute('aria-busy') === 'true');
    }

    mainLine() {
      const form = this.mainForm();
      if (!form || !this.mainBuyable()) return null;

      const data = new FormData(form);
      const id = parseInt(data.get('id'), 10);
      if (!id) return null;

      const line = { id, quantity: Math.max(1, parseInt(data.get('quantity'), 10) || 1) };
      const sellingPlan = data.get('selling_plan');
      if (sellingPlan) line.selling_plan = parseInt(sellingPlan, 10);
      const properties = {};
      data.forEach((value, key) => {
        const match = /^properties\[(.+)\]$/.exec(key);
        if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
      });
      if (Object.keys(properties).length) line.properties = properties;
      return line;
    }

    /* ---------- adding ---------- */

    isBusy(button) {
      return this.busy || !button || button.disabled || button.getAttribute('aria-busy') === 'true';
    }

    async quickAdd(item, button) {
      if (!item || this.isBusy(button)) return;
      const variant = this.selected(item);
      if (!variant || variant.dataset.available !== 'true') return;

      await this.run(button, [{ id: parseInt(variant.value, 10), quantity: this.quantity(item, variant) }], {
        openDrawer: this.dataset.afterAdd === 'drawer',
        errorEl: item.querySelector('[data-addon-error]'),
        done: `${this.describe(item, variant)} added to your bag.`,
      });
    }

    async addBoth(button) {
      if (this.isBusy(button)) return;
      // ADD TO CART is still adding: let it finish rather than put the main product in twice
      if (this.mainBusy()) return;
      const errorEl = this.querySelector('[data-addons-both-error]');
      const item = this.querySelector('[data-addon]');
      const variant = item && this.selected(item);

      const main = this.mainLine();
      if (!main) {
        this.showError(errorEl, 'Pick an available option above first.');
        return;
      }
      if (!variant || variant.dataset.available !== 'true') {
        this.showError(errorEl, `${(item && item.dataset.title) || 'This add-on'} is sold out.`);
        return;
      }

      await this.run(
        button,
        [main, { id: parseInt(variant.value, 10), quantity: this.quantity(item, variant) }],
        {
          openDrawer: true,
          errorEl,
          done: `${this.dataset.productTitle} and ${this.describe(item, variant)} added to your bag.`,
        }
      );
    }

    async run(button, lines, { openDrawer, errorEl, done }) {
      this.busy = true;
      this.reset(button);
      this.hideError(errorEl);
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');

      const started = performance.now();
      let response = null;
      let failure = null;
      try {
        try {
          response = await this.post(lines);
        } catch (error) {
          failure = error;
        }

        if (response) {
          // Tracking first, as product-form.js does: the lines are in the bag whatever the drawer
          // does next, and a drawer that fails to redraw must not cost the AddToCart events.
          this.publishAdds(response);
          try {
            this.renderCart(response, openDrawer, button);
          } catch (error) {
            console.error(error);
          }
        }

        const remaining = MIN_BUSY_MS - (performance.now() - started);
        if (remaining > 0) await wait(remaining);
      } finally {
        // whatever happened above, the strip must not stay locked
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
        this.busy = false;
      }

      if (response) {
        this.markAdded(button);
        this.say(done);
      } else {
        const message = (failure && failure.message) || this.genericError();
        this.showError(errorEl, message);
        this.say(message);
      }
    }

    genericError() {
      return (window.cartStrings && window.cartStrings.error) || 'Could not add to the bag. Try again.';
    }

    cart() {
      return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    sectionsToRender(cart) {
      if (cart && typeof cart.getSectionsToRender === 'function') return cart.getSectionsToRender();
      return [{ id: 'cart-icon-bubble' }];
    }

    async post(lines) {
      const sections = this.sectionsToRender(this.cart());
      const body = {
        items: lines,
        sections: sections.map((section) => section.id),
        sections_url: window.location.pathname,
      };
      const init = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      };
      if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
        init.signal = AbortSignal.timeout(10000);
      }

      let res;
      try {
        res = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add', init);
      } catch (error) {
        throw new Error(
          error && error.name === 'TimeoutError' ? 'The bag took too long to answer. Try again.' : this.genericError()
        );
      }

      let data = null;
      try {
        data = await res.json();
      } catch (error) {
        data = null;
      }
      if (!res.ok || !data || data.status) {
        throw new Error((data && (data.description || data.message)) || this.genericError());
      }
      return data;
    }

    renderCart(response, openDrawer, button) {
      const cart = this.cart();
      if (openDrawer && cart && typeof cart.renderContents === 'function') {
        // focus returns to this button when the drawer closes
        if (button && typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
        // cart-notification shows the line named by `key`, which a multi-line add response lacks
        const first = Array.isArray(response.items) ? response.items[0] : null;
        cart.renderContents(
          cart.tagName === 'CART-NOTIFICATION' && first && !response.key
            ? Object.assign({}, response, { key: first.key })
            : response
        );
        return;
      }
      if (!response.sections) return;

      // What cart-drawer.js renderContents does, minus open(): the bag is current, the page stays put.
      const parsed = {};
      this.sectionsToRender(cart).forEach((section) => {
        if (cart && cart.tagName === 'CART-NOTIFICATION' && section.id !== 'cart-icon-bubble') return;
        const html = response.sections[section.id];
        if (!html) return;
        const target = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id) || document.querySelector(`[id*="${section.id}"]`);
        if (!target) return;
        parsed[section.id] = parsed[section.id] || new DOMParser().parseFromString(html, 'text/html');
        const source = parsed[section.id].querySelector(section.selector || '.shopify-section');
        target.innerHTML = source ? source.innerHTML : '';
      });
      if (cart) {
        cart.classList.remove('is-empty');
        const inner = cart.querySelector('.drawer__inner');
        if (inner) inner.classList.remove('is-empty');
      }
    }

    publishAdds(response) {
      const events = pubSubEvents();
      if (!events || typeof publish !== 'function') return;
      const lines = Array.isArray(response.items) ? response.items : [response];
      lines.forEach((line) => {
        try {
          publish(events.cartUpdate, {
            source: 'product-form',
            origin: 'notice-addons',
            productVariantId: String(line.variant_id || line.id),
            cartData: line,
          });
        } catch (error) {
          // a listener that throws must not cost the next line its events
          console.error(error);
        }
      });
    }

    /* ---------- feedback ---------- */

    setLabel(button, text) {
      const label = button.querySelector('[data-addon-label], [data-addons-both-label]');
      if (label && text) label.textContent = text;
    }

    markAdded(button) {
      button.classList.add('is-added');
      this.setLabel(button, this.dataset.addedLabel);
      clearTimeout(this.timers.get(button));
      this.timers.set(
        button,
        setTimeout(() => this.reset(button), ADDED_MS)
      );
    }

    reset(button) {
      if (!button) return;
      clearTimeout(this.timers.get(button));
      this.timers.delete(button);
      if (!button.classList.contains('is-added')) return;
      button.classList.remove('is-added');
      this.setLabel(button, button.hasAttribute('data-addons-both') ? button.dataset.label : this.dataset.addLabel);
    }

    say(text) {
      if (!this.statusEl || !text) return;
      this.statusEl.textContent = '';
      requestAnimationFrame(() => {
        this.statusEl.textContent = text;
      });
    }

    showError(element, message) {
      if (!element || !message) return;
      element.textContent = message;
      element.hidden = false;
      clearTimeout(this.timers.get(element));
      this.timers.set(
        element,
        setTimeout(() => this.hideError(element), ERROR_MS)
      );
    }

    hideError(element) {
      if (!element) return;
      clearTimeout(this.timers.get(element));
      this.timers.delete(element);
      element.hidden = true;
      element.textContent = '';
    }
  }

  customElements.define('notice-addons', NoticeAddons);
})();
